import React from "react";
import { Link } from "react-router-dom";
import { FiMail, FiPhone, FiMapPin, FiFacebook, FiTwitter, FiArrowRight, FiClock, FiMessageCircle } from 'react-icons/fi';

export default function Footer() {
  return (
    <footer className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-gray-200">
      {/* Decorative Top Wave */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white dark:fill-slate-950"></path>
        </svg>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 pt-20 pb-10">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
          
          {/* Company Info - 1st Column */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-3">
                  Renerg (R) Ltd
                </h3>
                <p className="text-slate-400 leading-relaxed text-sm">
                  Leading green solution company in Rwanda, providing innovative solar energy solutions and sustainable technologies for over a decade across 12 African countries.
                </p>
              </div>
              
              <div className="flex items-start space-x-3 text-slate-400 hover:text-teal-400 transition-colors duration-300">
                <FiMapPin className="mt-1 flex-shrink-0" size={18} />
                <div className="text-sm">
                  <p className="font-medium text-white">Headquarters</p>
                  <p>Nyarugenge, Kigali</p>
                  <p>Rwanda, East Africa</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links - 2nd Column */}
          <div className="lg:col-span-1">
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-teal-400 to-transparent"></span>
            </h4>
            <ul className="space-y-3">
              {['About Us', 'Our Services', 'Projects', 'Careers', 'Contact'].map((item) => (
                <li key={item}>
                  <Link 
                    to="#" 
                    className="text-slate-400 hover:text-teal-400 transition-all duration-300 flex items-center group text-sm"
                  >
                    <FiArrowRight className="mr-2 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0 transition-all duration-300" size={14} />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - 3rd Column */}
          <div className="lg:col-span-1">
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Our Services
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-teal-400 to-transparent"></span>
            </h4>
            <ul className="space-y-3">
              {['Solar Installation', 'Energy Storage', 'Smart Buildings', 'E-Vehicle Solutions', 'Maintenance'].map((item) => (
                <li key={item}>
                  <Link 
                    to="#" 
                    className="text-slate-400 hover:text-teal-400 transition-all duration-300 flex items-center group text-sm"
                  >
                    <FiArrowRight className="mr-2 opacity-0 group-hover:opacity-100 -ml-6 group-hover:ml-0 transition-all duration-300" size={14} />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch - 4th Column */}
          <div className="lg:col-span-1">
            <h4 className="text-lg font-semibold mb-6 relative inline-block">
              Get In Touch
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gradient-to-r from-teal-400 to-transparent"></span>
            </h4>
            <div className="space-y-4">
              {/* Phone */}
              <div className="flex items-start space-x-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="p-2 bg-slate-800 rounded-lg group-hover:bg-teal-500/10 transition-colors duration-300 flex-shrink-0 mt-1">
                  <FiPhone size={16} className="text-teal-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Phone</p>
                  <a 
                    href="tel:+250791502063" 
                    className="text-slate-400 hover:text-teal-400 transition-colors duration-300 text-sm"
                  >
                    +250 791 502 063
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="p-2 bg-slate-800 rounded-lg group-hover:bg-teal-500/10 transition-colors duration-300 flex-shrink-0 mt-1">
                  <FiMail size={16} className="text-teal-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Email</p>
                  <a 
                    href="mailto:renergrwanda@gmail.com" 
                    className="text-slate-400 hover:text-teal-400 transition-colors duration-300 text-sm break-all"
                  >
                    renerghrwanda@gmail.com
                  </a>
                </div>
              </div>

              {/* Business Hours */}
              <div className="flex items-start space-x-3 group hover:translate-x-1 transition-transform duration-300">
                <div className="p-2 bg-slate-800 rounded-lg group-hover:bg-teal-500/10 transition-colors duration-300 flex-shrink-0 mt-1">
                  <FiClock size={16} className="text-teal-400" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Business Hours</p>
                  <p className="text-slate-400 text-sm">Mon - Fri: 8:00 - 17:00</p>
                  <p className="text-slate-400 text-sm">Sat: 9:00 - 13:00</p>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Divider with gradient */}
        <div className="mt-8 mb-6">
          <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent"></div>
        </div>
        <hr className="mb-4 border-t-1 border-teal-600"/>
        {/* Bottom Footer */}
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6 items-center">
          
          {/* Copyright */}
          <div className="text-center lg:text-left">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} <span className="text-teal-400 font-medium">Renerg (R) Ltd</span>. All rights reserved.
            </p>
          </div>

          {/* Social Media */}
          <div className="flex justify-center items-center space-x-3">
            <Link 
              to="https://www.facebook.com/renergrltd/" 
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-3 bg-slate-800 rounded-lg hover:bg-gradient-to-br hover:from-teal-500 hover:to-emerald-500 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-teal-500/50"
              title="Facebook"
            >
              <FiFacebook size={18} className="group-hover:text-white transition-colors" />
            </Link>
            <Link 
              to="https://twitter.com/renergrwanda" 
              target="_blank"
              rel="noopener noreferrer"
              className="group relative p-3 bg-slate-800 rounded-lg hover:bg-gradient-to-br hover:from-teal-500 hover:to-emerald-500 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-teal-500/50"
              title="Twitter"
            >
              <FiTwitter size={18} className="group-hover:text-white transition-colors" />
            </Link>
            <Link 
              to="mailto:renergrwanda@gmail.com" 
              className="group relative p-3 bg-slate-800 rounded-lg hover:bg-gradient-to-br hover:from-teal-500 hover:to-emerald-500 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-teal-500/50"
              title="Email"
            >
              <FiMail size={18} className="group-hover:text-white transition-colors" />
            </Link>
          </div>

          {/* Developer Credit */}
          <div className="text-center lg:text-right">
            <p className="text-slate-400 text-sm">
              Developed by{' '}
              <a 
                href="https://enoveta.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-400 hover:text-teal-300 font-medium transition-colors duration-300 hover:underline"
              >
                Enoveta
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>
    </footer>
  );
}